/**
 * Reusable series list + add-combobox. Renders the ordered
 * list of picked columns as chips - optional color swatch, optional fit button,
 * a remove button, and HTML5 drag-to-reorder - plus a `<column-combobox>` to
 * add another. The Y-left, Y-right, scatter-Y, and histogram pickers are all
 * this one element with different props.
 *
 * It owns no state: items + the candidate pool come in as props, and every
 * mutation is reported through a callback so the `plotState` atom stays the
 * single source of truth.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { wrapColName } from '../../utils/columns.ts';
import type { ColumnInfo } from '../../api/types.ts';
import type { PoolColumn } from '../ship/ship-columns.ts';
import type { FitLabel } from './plot-ui-state.ts';
import '../../ui/column-combobox.ts';

@customElement('series-picker')
export class SeriesPicker extends LightElement {
  /** Picked column names, in display order. */
  @property({ attribute: false }) items: string[] = [];
  /** Candidate columns for the add combobox (parent pre-filters by type). */
  @property({ attribute: false }) pool: ColumnInfo[] = [];
  /** Extra names to exclude from the add combobox (e.g. X column, color-by). */
  @property({ attribute: false }) exclude: string[] = [];
  @property() addPlaceholder = '+ Add column…';
  @property({ type: Boolean }) showColor = false;
  @property({ type: Boolean }) showFit = false;

  /** Color for a chip at list position `idx`. */
  @property({ attribute: false }) colorOf: (name: string, idx: number) => string =
    () => 'var(--accent)';
  @property({ attribute: false }) hasFit: (name: string) => boolean = () => false;
  /** Per-series fit label (eq + R²/error) shown under the chip, from `/plot`. */
  @property({ attribute: false }) fitLabelOf: (name: string) => FitLabel | undefined =
    () => undefined;

  @property({ attribute: false }) addItem: (name: string) => void = () => {};
  @property({ attribute: false }) removeItem: (name: string) => void = () => {};
  @property({ attribute: false }) reorder: (from: number, to: number) => void = () => {};
  @property({ attribute: false }) recolor: (name: string, hex: string) => void = () => {};
  @property({ attribute: false }) openFit: (name: string, el: HTMLElement) => void = () => {};

  private dragIndex = -1;

  private candidatePool(): PoolColumn[] {
    const used = new Set([...this.items, ...this.exclude]);
    return this.pool.filter((c) => !used.has(c.name));
  }

  /* ── Drag-to-reorder ──────────────────────────────────── */

  private onDragStart(e: DragEvent, idx: number): void {
    this.dragIndex = idx;
    e.dataTransfer?.setData('text/plain', String(idx));
    (e.currentTarget as HTMLElement).classList.add('dragging');
  }

  private onDragEnd(e: DragEvent): void {
    this.dragIndex = -1;
    (e.currentTarget as HTMLElement).classList.remove('dragging');
    this.querySelectorAll('.series-chip').forEach((el) =>
      el.classList.remove('drop-above', 'drop-below'),
    );
  }

  private onDragOver(e: DragEvent, idx: number): void {
    if (this.dragIndex < 0 || this.dragIndex === idx) return;
    e.preventDefault();
    const chip = e.currentTarget as HTMLElement;
    chip.classList.toggle('drop-above', idx < this.dragIndex);
    chip.classList.toggle('drop-below', idx > this.dragIndex);
  }

  private onDragLeave(e: DragEvent): void {
    (e.currentTarget as HTMLElement).classList.remove('drop-above', 'drop-below');
  }

  private onDrop(e: DragEvent, idx: number): void {
    e.preventDefault();
    const from = this.dragIndex;
    this.dragIndex = -1;
    this.querySelectorAll('.series-chip').forEach((el) =>
      el.classList.remove('drop-above', 'drop-below'),
    );
    if (from < 0 || from === idx) return;
    this.reorder(from, idx);
  }

  /* ── Rendering ────────────────────────────────────────── */

  private renderFitLabel(col: string): TemplateResult | typeof nothing {
    if (!this.showFit) return nothing;
    const label = this.fitLabelOf(col);
    if (!label) return nothing;
    if (label.error) return html`<div class="chip-fit-eq error">fit failed: ${label.error}</div>`;
    const r2 = label.r2 != null ? ` · R²=${label.r2.toFixed(3)}` : '';
    return html`<div class="chip-fit-eq">${(label.eq ?? '') + r2}</div>`;
  }

  private renderChip(col: string, idx: number): TemplateResult {
    const color = this.colorOf(col, idx);
    return html`
      <div
        class="series-chip"
        draggable="true"
        style=${`--chip-color: ${color}`}
        @dragstart=${(e: DragEvent) => this.onDragStart(e, idx)}
        @dragend=${this.onDragEnd}
        @dragover=${(e: DragEvent) => this.onDragOver(e, idx)}
        @dragleave=${this.onDragLeave}
        @drop=${(e: DragEvent) => this.onDrop(e, idx)}
      >
        <span class="chip-drag" title="Drag to reorder">≡</span>
        ${this.showColor
          ? html`<label class="chip-color-swatch" style=${`background:${color}`} title="Click to recolor">
              <input
                type="color"
                .value=${color}
                @input=${(e: Event) => this.recolor(col, (e.target as HTMLInputElement).value)}
              />
            </label>`
          : nothing}
        <span class="chip-name">${wrapColName(col)}</span>
        ${this.showFit
          ? html`<button
              class="chip-fit-btn ${this.hasFit(col) ? 'active' : ''}"
              title="Curve fit"
              @click=${(e: Event) => {
                e.stopPropagation();
                this.openFit(col, e.currentTarget as HTMLElement);
              }}
            >
              ∿
            </button>`
          : nothing}
        <button class="chip-remove" title="Remove" @click=${() => this.removeItem(col)}>×</button>
        ${this.renderFitLabel(col)}
      </div>
    `;
  }

  override render(): TemplateResult {
    return html`
      <!-- No placeholder when the list is empty: the add box below already
           says what goes here, and a second line saying "nothing yet" only
           padded the sidebar. -->
      <div class="series-list">${this.items.map((c, i) => this.renderChip(c, i))}</div>
      <column-combobox
        .columns=${this.candidatePool()}
        .value=${''}
        ?clearOnPick=${true}
        placeholder=${this.addPlaceholder}
        .select=${(name: string) => name && this.addItem(name)}
      ></column-combobox>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'series-picker': SeriesPicker;
  }
}
